import React, { MouseEventHandler } from "react";

type ActiveStepProp = {
  name: string;
  active: boolean;
  href?: undefined;
  onClick?: undefined;
};

type NotNavigateableStepProp = {
  name: string;
  active?: boolean;
  href?: undefined;
  onClick?: undefined;
};

type HrefStepProp = {
  name: string;
  active?: boolean;
  href: string;
  onClick?: undefined;
};

type OnClickStepProp = {
  name: string;
  active?: boolean;
  href?: undefined;
  onClick: MouseEventHandler<HTMLButtonElement>;
};

// Type for å tvinge både asElement og hrefAttribute til å brukes samtidig
// Mangler en av de får man en ts-feil
type asElementHrefAttribute = (
  { asElement: any; hrefAttribute: string }
  | { asElement?: never; hrefAttribute?: never }
  )

type CustomWrapperStepProp = {
  name: string
  href?: string
  active?: boolean
} & asElementHrefAttribute;

export type StepProp =
  | ActiveStepProp
  | HrefStepProp
  | OnClickStepProp
  | NotNavigateableStepProp
  | CustomWrapperStepProp;

type StepIndicatorProps = {
  routes: StepProp[]
  className?: string
  ariaLabel: string
};
// Type guards
// https://www.typescriptlang.org/docs/handbook/advanced-types.html#user-defined-type-guards
function isCustomWrapperStepProp(step: StepProp): step is CustomWrapperStepProp {
  return (step as CustomWrapperStepProp).asElement !== undefined
}

function isActiveStepProp(step: StepProp): step is ActiveStepProp {
  return (step as ActiveStepProp).active
}

function isOnClickStepProp(step: StepProp): step is OnClickStepProp {
  return (step as OnClickStepProp).onClick !== undefined
}

function renderSpan(navn: string) {
  return (
    <span className="step-viewer__title">
      {navn}
    </span>
  )
}

function viewSteg(step: StepProp): JSX.Element {
  // Separate funksjoner for å kunne typesette argument som spesifikk stegtype
  function viewActive(activeStepProp: ActiveStepProp) {
    return (
      <li
        className="step--active"
        aria-current="step"
      >
        {renderSpan(activeStepProp.name)}
      </li>
    );
  }

  function viewHref(hrefStepProp: HrefStepProp) {
    return (
      <li
        className="step--link"
      >
        <a className="step-viewer__link" href={hrefStepProp.href}>
          {renderSpan(hrefStepProp.name)}
        </a>
      </li>
    );
  }

  function viewOnClick(onClickStepProp: OnClickStepProp) {
    return (
      <li className="step--link">
        <button
          type="button"
          className="step-viewer__link"
          onClick={event => {
            event.preventDefault();
            onClickStepProp.onClick(event);
          }}
        >
          {renderSpan(onClickStepProp.name)}
        </button>
      </li>
    );
  }

  function viewNotNavigateable(route: NotNavigateableStepProp) {
    return (
      <li>
        {renderSpan(route.name)}
      </li>
    );
  }

  function viewCustomWrapper(customWrapperStepProp: CustomWrapperStepProp) {
    const showAsLink = customWrapperStepProp.href && !customWrapperStepProp.active
    const Wrapper = customWrapperStepProp.asElement;

    let liCssClass = ""
    if (customWrapperStepProp.active) {
      liCssClass += "step--active"
    }
    if (showAsLink) {
      liCssClass += "step--link"
    }

    return (
      <li
        className={liCssClass}
        {...(customWrapperStepProp.active && { "aria-current": "step" })}
      >
        <Wrapper
          className={showAsLink  ? "step-viewer__link" : ""}
          {...(customWrapperStepProp.hrefAttribute && {[customWrapperStepProp.hrefAttribute]: customWrapperStepProp.href})}
        >
          <span className="step-viewer__title">
            {customWrapperStepProp.name}
          </span>
        </Wrapper>
      </li>
    )
  }

  if (isCustomWrapperStepProp(step)) {
    return viewCustomWrapper(step)
  }
  if (isActiveStepProp(step)) {
    return viewActive(step);
  }
  if (step.href !== undefined) {
    return viewHref(step);
  }
  if (isOnClickStepProp(step)) {
    return viewOnClick(step);
  }
  return viewNotNavigateable(step);
}

export function SVVStepIndicator({ routes, className, ariaLabel }: StepIndicatorProps) {
  return (
    <nav
      className={className || ""}
      aria-label={ariaLabel}
    >
      <ol className={`${className || ""} step-viewer`}>
        {routes &&
          routes.length > 0 &&
          routes.map((route: StepProp, idx: number) => (
            <React.Fragment key={`${route.name}-${idx}`}>
              {viewSteg(route)}
            </React.Fragment>
          ))}
      </ol>
    </nav>
  );
}
